import { Box, IconButton, ListItemButton, Grid2 as Grid, Stack, useTheme, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  MentionsInput as ReactMentionsInput,
  Mention,
  type OnChangeHandlerFunc,
  type MentionItem,
} from "react-mentions";
import React, { useEffect } from "react";
import { useUsers } from "../../hooks/useUsers";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { ToastContext } from "../../context/ToastContext";
import { useToggle } from "../../hooks/useToggle";
import { CommentPartModel } from "@attelas/shared";

const COMMENT_MAX_LENGTH = 10 * 100;
const SINGLE_LINE_HEIGHT = 39;

function useIsMultiLine(textareaRef: React.RefObject<HTMLTextAreaElement | null>) {
  const [isMultiLine, setIsMultiLine] = React.useState(false);

  useEffect(() => {
    const textarea = textareaRef.current;

    const resizeObserver = new ResizeObserver(() => {
      const currentTextarea = textareaRef.current;
      setIsMultiLine(!!currentTextarea && currentTextarea.clientHeight > SINGLE_LINE_HEIGHT);
    });

    if (textarea) {
      resizeObserver.observe(textarea);
    }

    return () => {
      if (textarea) {
        resizeObserver.unobserve(textarea);
      }
    };
  }, [textareaRef]);

  return isMultiLine;
}

export interface MentionsInputProps {
  addComment?: (params: { comment: CommentPartModel[] }) => Promise<void>;
}

export function MentionsInput({ addComment }: MentionsInputProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { showToast } = React.useContext(ToastContext);
  const { users } = useUsers();
  const { currentUser } = useCurrentUser();
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState("");
  const [plainText, setPlainText] = React.useState("");
  const [mentions, setMentions] = React.useState<MentionItem[]>([]);
  const [hovered, hover, unHover] = useToggle(false);
  const [focused, focus, blur] = useToggle(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const isMultiLine = useIsMultiLine(textareaRef);

  const comment = trimComment(computeComment(plainText, mentions));

  const borderColor = () => {
    if (isDarkMode) {
      return focused ? "#90CAF9" : hovered ? "#E0E0E0" : "#424242";
    } else {
      return focused ? "#005AF8" : hovered ? "#222131" : "#C7C6D2";
    }
  };

  const suggestionItemBackground = (focused: boolean) => {
    if (isDarkMode) {
      return focused ? "#90CAF9" : "#424242";
    } else {
      return focused ? "#F0F0F0" : undefined;
    }
  };

  const mentionsData =
    users
      ?.filter(({ id }) => id !== currentUser?.id)
      .map(({ id, fullName }) => ({
        id,
        display: fullName,
      })) || [];

  const onChange: OnChangeHandlerFunc = (event, _, plainText, mentions) => {
    setValue(event.target.value);
    setPlainText(plainText);
    setMentions(mentions);
  };

  const focusTextarea = () => {
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const onSend = async () => {
    if (!comment.length) {
      setValue("");
      setPlainText("");
      setMentions([]);
      focusTextarea();
      return;
    }

    if (plainText.length > COMMENT_MAX_LENGTH) {
      showToast(`Message should not be longer than ${COMMENT_MAX_LENGTH} characters`, "error");
      focusTextarea();
      return;
    }

    try {
      setLoading(true);
      if (addComment) {
        await addComment({ comment });
      }
      setValue("");
      setPlainText("");
      setMentions([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      focusTextarea();
    }
  };

  const renderSendButton = () => {
    const sendButton = (
      <IconButton
        color="primary"
        size="small"
        onClick={onSend}
        disabled={!comment.length || loading}
        disableRipple={true}
        sx={{
          flex: "0 0 auto",
          alignSelf: "end",
          minWidth: 0,
          height: "39px",
          paddingLeft: "2px",
          paddingRight: "4px",
        }}
      >
        <SendIcon sx={{ fontSize: "1rem" }} />
      </IconButton>
    );

    if (isMultiLine) {
      return (
        <Grid container={true} alignItems="center" justifyContent="space-between" pl="14px">
          <Typography color="textSecondary">
            {plainText.length} / {COMMENT_MAX_LENGTH} characters
          </Typography>
          {sendButton}
        </Grid>
      );
    }

    return sendButton;
  };

  return (
    <Stack direction={isMultiLine ? "column" : "row"} borderRadius="4px" border={`1px solid ${borderColor()}`}>
      <ReactMentionsInput
        value={value}
        onChange={onChange}
        inputRef={textareaRef}
        style={{
          minWidth: 0,
          flex: "1 1 auto",
          control: {
            borderRadius: isMultiLine ? "4px" : "4px 0 0 4px",
            background: isDarkMode ? "#000000" : "#FFFFFF",
            fontSize: "13px",
            lineHeight: "19px",
            padding: isMultiLine ? "8px 22px 8px 14px" : "8px 0px 8px 14px",
            maxHeight: "108px",
          },
          input: {
            borderRadius: isMultiLine ? "4px" : "4px 0 0 4px",
            padding: isMultiLine ? "8px 22px 8px 14px" : "8px 0px 8px 14px",
            maxHeight: "108px",
            fontSize: "13px",
            lineHeight: "19px",
            overflowY: "auto",
            outline: "none",
            border: "none",
            color: isDarkMode ? "#FFFFFF" : "#222131",
          },
          suggestions: {
            borderRadius: "4px",
          },
        }}
        forceSuggestionsAboveCursor={true}
        allowSpaceInQuery={true}
        disabled={loading}
        customSuggestionsContainer={(children) => (
          <Box
            sx={{
              boxShadow: 1,
              borderRadius: 1,
              overflowX: "hidden",
              overflowY: "auto",
              maxHeight: "212px",
            }}
          >
            {children}
          </Box>
        )}
        placeholder="Type a message - tag others with @"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onSend();
          }
        }}
        onMouseEnter={hover}
        onMouseLeave={unHover}
        onFocus={focus}
        onBlur={blur}
      >
        <Mention
          trigger="@"
          data={mentionsData}
          renderSuggestion={(suggestion, _1, _2, _3, focused) => (
            <Box minWidth={100} maxWidth={200}>
              <ListItemButton sx={{ backgroundColor: suggestionItemBackground(focused) }}>
                {suggestion.display}
              </ListItemButton>
            </Box>
          )}
        />
      </ReactMentionsInput>
      {renderSendButton()}
    </Stack>
  );
}

function computeComment(plainText: string, mentions: MentionItem[]): CommentPartModel[] {
  if (!mentions.length)
    return [
      {
        content: plainText,
        type: "text",
      },
    ];

  let currentIndex = 0;
  const result: CommentPartModel[] = [];

  for (const { id, plainTextIndex, display } of mentions) {
    if (currentIndex < plainTextIndex) {
      result.push({
        content: plainText.slice(currentIndex, plainTextIndex),
        type: "text",
      });
    }
    result.push({
      content: id,
      type: "mention",
    });
    currentIndex = plainTextIndex + display.length;
  }

  if (currentIndex < plainText.length) {
    result.push({
      content: plainText.slice(currentIndex),
      type: "text",
    });
  }

  return result;
}

function trimComment(commentParts: CommentPartModel[]) {
  if (!commentParts.length) return [];

  let i = 0;
  let j = commentParts.length - 1;

  const isEmpty = (commentPart: CommentPartModel) => {
    return commentPart.type === "text" && !commentPart.content.trim();
  };

  while (i <= j) {
    if (!isEmpty(commentParts[i]) && !isEmpty(commentParts[j])) {
      break;
    }

    if (isEmpty(commentParts[i])) {
      i++;
    }

    if (isEmpty(commentParts[j])) {
      j--;
    }
  }

  const comments = commentParts.slice(i, j + 1);

  if (!comments.length) return [];

  const startPart = comments[0];
  if (startPart.type === "text") {
    startPart.content = startPart.content.trimStart();
  }

  const endPart = comments[comments.length - 1];
  if (endPart.type === "text") {
    endPart.content = endPart.content.trimEnd();
  }

  return comments;
}
